import { EnhancedTableToolbar } from '@/components/core/async-data-table/async-data-table-toolbar';
import FilterHeader from '@/components/core/async-data-table/filter-header';
import { TablePagination } from '@/components/core/async-data-table/table-pagination';
import { DatableColumnType } from '@/components/core/async-data-table/type';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ProductVariant, VariantStockSearchItem } from '@/types/product-type';
import { DollarSign, Loader2, Package, TrendingUp } from 'lucide-react';
import React, { useMemo } from 'react';
import { VariantFlatRow } from './variant-flat-row';
import { EditVariantDialog } from '@/components/products/form/section/variant/edit-variant-dialog';
import { useVariantEdit } from '@/hooks/product/use-variant-edit';
import { useProductStockAdjustment } from '@/hooks/product/use-product-stock-adjustment';
import { AdjustInStockDialog } from '@/pages/stock/components/AdjustInStockDialog';
import { AdjustOutStockDialog } from '@/pages/stock/components/AdjustOutStockDialog';
import { useVariantContext } from '../context/variant-context';

interface VariantFlatTableProps {
  variants: VariantStockSearchItem[];
  isLoading?: boolean;
  pagination?: {
    totalElements: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    isEmpty: boolean;
  };
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  onRefresh?: () => void;
}

// Define searchable columns for variant filtering — only product, variant, and sale_code (SKU)
const variantFilterColumns: DatableColumnType[] = [
  {
    name: 'p.name',
    id: 'productName',
    label: 'Product',
    searchable: true,
    datatype: 'string' as const,
  },
  {
    name: 'v.name',
    id: 'variantName',
    label: 'Variant',
    searchable: true,
    datatype: 'string' as const,
  },
  {
    name: 'saleCode',
    id: 'saleCode',
    label: 'Sale Code (SKU)',
    searchable: true,
    datatype: 'string' as const,
  },
];

export function VariantFlatTable({
  variants,
  isLoading = false,
  pagination,
  page,
  setPage,
  pageSize,
  setPageSize,
  onRefresh,
}: VariantFlatTableProps) {
  // Get filter state from ProductContext (same pattern as AsyncDataTable)
  const { searchQuery, setSearchQuery, propSearch, setPropSearch } = useVariantContext();

  // Extract searchable columns for FilterHeader
  const filterColumn = useMemo(
    () => variantFilterColumns.filter((col) => col.searchable === true),
    [],
  );

  // Use the variant edit hook
  const firstVariant = variants[0];
  const productId = firstVariant?.productId?.toString() || '';

  const {
    isEditDialogOpen,
    setIsEditDialogOpen,
    editingVariant,
    isUpdating,
    handleEdit,
    handleEditSubmit,
    handleCancel,
  } = useVariantEdit({
    productId,
    onSuccess: onRefresh,
  });

  // Stock adjustment logic
  const {
    adjustInDialogState,
    adjustOutDialogState,
    handleAdjustIn,
    handleAdjustOut,
    setAdjustInDialogState,
    setAdjustOutDialogState,
  } = useProductStockAdjustment();

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  function handleEditVariant(variant: ProductVariant): void {
    handleEdit(variant);
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Filter Toolbar — same pattern as Customer page (AsyncDataTable + FilterHeader) */}
        <EnhancedTableToolbar
          FilterComponent={FilterHeader}
          filterColumn={filterColumn}
          searchFunction={setSearchQuery}
          searchValue={searchQuery}
          propSearch={propSearch}
          setPropSearch={setPropSearch}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading variants...</span>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm w-full max-w-full overflow-x-auto">
          <div className="min-w-full">
            <Table className="w-full">
              <TooltipProvider>
                <TableHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold text-slate-700 py-2 px-2 text-xs sm:text-sm">
                      Product
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 py-2 px-2 text-xs sm:text-sm">
                      Variant / SKU
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 py-2 px-1.5 sm:px-2 text-center text-xs sm:text-sm">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center gap-1 sm:gap-1.5 cursor-help">
                            <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                            <span className="text-xs sm:text-sm whitespace-nowrap">Stock</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs space-y-1">
                            <div>
                              Shows inflows (received, adjustments in) and outflows (sold,
                              adjustments out)
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="hidden sm:table-cell font-semibold text-slate-700 py-2 px-2 text-right text-xs sm:text-sm">
                      <div className="flex items-center justify-end gap-1 sm:gap-1.5">
                        <DollarSign className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                        <span className="text-xs sm:text-sm whitespace-nowrap">Price</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 py-2 px-2 text-center text-xs sm:text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
              </TooltipProvider>
              <TableBody>
                {variants.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-sm text-slate-400">
                      No variants found
                    </td>
                  </tr>
                ) : (
                  variants.map((variant) => (
                    <VariantFlatRow
                      key={variant.id}
                      variant={variant}
                      onEditVariant={handleEditVariant}
                      onAdjustIn={handleAdjustIn}
                      onAdjustOut={handleAdjustOut}
                    />
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              count={pagination?.totalElements ?? 0}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
            {/* Edit Variant Dialog */}
            <EditVariantDialog
              open={isEditDialogOpen}
              onOpenChange={setIsEditDialogOpen}
              editingVariant={editingVariant}
              productId={productId}
              productCode={''}
              onSubmit={handleEditSubmit}
              onCancel={handleCancel}
              isLoading={isUpdating}
            />

            {/* Adjust In Dialog */}
            {adjustInDialogState.selectedStock && (
              <AdjustInStockDialog
                isOpen={adjustInDialogState.isOpen}
                onClose={() => setAdjustInDialogState({ isOpen: false, selectedStock: null, dailyStockId: null })}
                onSuccess={onRefresh}
                stock={adjustInDialogState.selectedStock}
                sku={adjustInDialogState.selectedStock.sku}
              />
            )}

            {/* Adjust Out Dialog */}
            {adjustOutDialogState.selectedStock && (
              <AdjustOutStockDialog
                isOpen={adjustOutDialogState.isOpen}
                onClose={() => setAdjustOutDialogState({ isOpen: false, selectedStock: null, dailyStockId: null })}
                onSuccess={onRefresh}
                stock={adjustOutDialogState.selectedStock}
                sku={adjustOutDialogState.selectedStock.sku}
              />
            )}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}
